import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Button } from "nlib/ui";
import { getSelectedBusinessData, getUserBusinessesData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { getUserRole } from "selectors/user";
import { useDispatch, useSelector } from "react-redux";
import ActivityActions from "actions/ActivityActions";
import AddQuickBooksDesktopStep from "./lib/AddQuickBooksDesktopStep";
import Async from "utils/Async";
import BusinessDescriptionStep from "./lib/BusinessDescriptionStep";
import BusinessesActions from "actions/BusinessesActions";
import Constants from "const/Constants";
import IntegrationServices from "const/IntegrationServices";
import InviteUserStep from "./lib/InviteUserStep";
import OrganizationsActions from "actions/OrganizationsActions";
import QuickBooksCompleteStep from "./lib/QuickBooksCompleteStep";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import RpaSettingUpStep from "./lib/RpaSettingUpStep";
import RpaSettingsStep from "./lib/RpaSettingsStep";
import SelectServiceStep from "./lib/SelectServiceStep";
import SyncEntitiesStep from "./lib/SyncEntitiesStep";
import UiRoutes from "const/UiRoutes";
import Urls from "utils/Urls";
import UserRoles from "const/UserRoles";
import WhoAskedStep from "./lib/WhoAskedStep";
import classNames from "classnames";
import useEnvVars from "hooks/useEnvVars";

const STEP_FIRST = 0;

const STEP_SECOND = 1;

const STEP_THIRD = 2;

const STEP_FOURTH = 3;

const STEP_FIFTH = 4;

const STEP_SIXTH = 5;

const STEP_SEVENTH = 6;

const Onboarding = (props) => {
  const [{ error: integrationError }] = useEnvVars();

  const { service: serviceToConnect, rpaMode } = props;

  const dispatch = useDispatch();

  const [newBusinessId, setNewBusinessId] = useState(null);

  const { error: envVarsError } = useMemo(() => {
    return Urls.parseQueryString(window.location.search);
  }, []);

  const { uiTexts, messages } = useSelector(getTextsData);

  const userRole = useSelector(getUserRole);

  const allUserBusinessesData = useSelector(getUserBusinessesData);

  const {
    id: selectedBusinessId,
    extraData = {},
    settings: {
      rpaStartFrom,
      rpaTransactionTypes,
      rpaAutoAskClient,
      businessDescription
    } = {}
  } = useSelector(getSelectedBusinessData);

  const {
    integrationService,
    lastParsingRpaAt,
    lastReconciliationRpaAt,
    lastEntitiesAutoSetAt,
    uncategorizedRpaEnabled,
    integrationServiceConnected
  } = extraData;

  const quickBooksService = (serviceToConnect || integrationService) === IntegrationServices.QUICK_BOOKS.value;

  const quickBooksDesktopService = serviceToConnect === IntegrationServices.QUICK_BOOKS_DESKTOP.value;

  const currentStep = useMemo(() => {
    if (envVarsError || (!serviceToConnect && integrationService && !integrationServiceConnected)) return STEP_FIRST;
    if (!selectedBusinessId || (!rpaMode && (!newBusinessId || !lastEntitiesAutoSetAt))) return STEP_SECOND;
    if (businessDescription === undefined) return STEP_THIRD;
    if (rpaStartFrom === undefined || rpaTransactionTypes === undefined) return STEP_FOURTH;
    if (rpaAutoAskClient === undefined) return STEP_FIFTH;
    if (!uncategorizedRpaEnabled) return STEP_SIXTH;

    return STEP_SEVENTH;
  }, [
    serviceToConnect,
    integrationService,
    integrationServiceConnected,
    uncategorizedRpaEnabled,
    rpaAutoAskClient,
    rpaStartFrom,
    rpaTransactionTypes,
    businessDescription,
    selectedBusinessId,
    rpaMode,
    newBusinessId,
    lastEntitiesAutoSetAt,
    envVarsError
  ]);

  const stepsComponents = useMemo(() => {
    return [
      SelectServiceStep,
      quickBooksDesktopService ? AddQuickBooksDesktopStep : SyncEntitiesStep,
      ...(
        rpaMode
          ? [
            BusinessDescriptionStep,
            RpaSettingsStep,
            WhoAskedStep,
            InviteUserStep,
            RpaSettingUpStep
          ]
          : [quickBooksService && currentStep > STEP_SECOND && QuickBooksCompleteStep])
    ].filter(Boolean);
  }, [currentStep, quickBooksDesktopService, quickBooksService, rpaMode]);

  const [onboardingTitle] = rpaMode ? messages.onboardingRpaWorkflow : messages.onboardingRegularWorkflow;

  const businessUser = UserRoles.checkIsBusiness(userRole);

  const rpaDidRun = !!(lastParsingRpaAt || lastReconciliationRpaAt) && (rpaAutoAskClient !== undefined);

  const handleBackButtonClick = useCallback(() => {
    window.location.href = UiRoutes.MAIN;
  }, []);

  const fetchData = useCallback(async() => {
    if (!selectedBusinessId) return;
    await dispatch(OrganizationsActions.fetchOrganizationsList(true, true));
    await dispatch(BusinessesActions.fetchBusinessesList(false, true, true));
    await dispatch(
      ActivityActions.fetchActivityList(
        {},
        0,
        Constants.TABLE_PAGE_SIZE,
        false,
        true
      )
    );
  }, [dispatch, selectedBusinessId]);

  useEffect(() => {
    fetchData();

    return Async.setInterval(fetchData, Constants.DATA_LIST_UPDATE_INTERVAL);
  }, [fetchData]);

  return (
    <div className={classNames(Css.onboarding, rpaDidRun && currentStep === STEP_FOURTH && Css.reconnect)}>
      <div className={Css.container}>
        <div className={Css.card}>
          <div className={Css.title}>
            <div>{onboardingTitle}</div>
            {!businessUser && (allUserBusinessesData.length > 1 || !!integrationError) && (
              <Button light large outline className={Css.closeButton} onClick={handleBackButtonClick}>
                <Icons.ArrowLeft />
                <span>{uiTexts.back}</span>
              </Button>
            )}
          </div>
          {stepsComponents.map((StepComponent, index) => {
            return (
              <StepComponent
                key={String(`${index}`)}
                index={index}
                rpaMode={rpaMode}
                currentStep={currentStep}
                integrationService={integrationService}
                serviceToConnect={serviceToConnect}
                newBusinessId={newBusinessId}
                setNewBusinessId={setNewBusinessId} />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(Onboarding);
